import { t } from "@/i18n-js/instance";
import type { Event } from "@circle-react/types";
import { Icon } from "@circle-react-shared/Icon";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { Typography } from "@circle-react-uikit/Typography";
import { formatRecurringSchedule } from "../../../utils/formatRecurringSchedule";
import { AttendeeExperienceSection } from "../AttendeeExperienceSection";

interface ScheduleUpdateConfirmationModalProps {
  event?: Event | null;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ScheduleUpdateConfirmationModal = ({
  event,
  isOpen,
  onCancel,
  onConfirm,
}: ScheduleUpdateConfirmationModalProps) => {
  // Format schedule information from event data
  const formattedEventName = event?.name;
  const formattedOriginalSchedule = event
    ? formatRecurringSchedule({
        frequency: event.recurring_setting_attributes?.frequency || "",
        startsAt: event.event_setting_attributes?.starts_at || "",
        endsAt: event.recurring_setting_attributes?.ends_at,
        occurrences: event.recurring_setting_attributes?.occurrences,
      })
    : "";
  const formattedNewSchedule = formattedOriginalSchedule;

  return (
    <ConfirmationModal
      title={t("events.edit_modal.schedule_update_modal.title")}
      isOpen={isOpen}
      confirmText={t("events.edit_modal.schedule_update_modal.update_schedule")}
      cancelText={t("events.edit_modal.schedule_update_modal.back")}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={onConfirm}
      size="lg"
    >
      <div className="space-y-6">
        {/* Schedule Details Section */}
        <div className="flex flex-col rounded-xl border p-4 pt-3 shadow-lg">
          <div className="border-primary flex items-center gap-2 border-b pb-4">
            <Typography.LabelMd weight="semibold" textTransform="capitalize">
              {formattedEventName}
            </Typography.LabelMd>
            <Typography.BodyMd color="text-secondary">
              {t("events.edit_modal.schedule_update_modal.updated_schedule")}
            </Typography.BodyMd>
          </div>

          {/* Original Series */}
          <div className="border-primary relative flex flex-col gap-2 border-b pb-6 pt-4">
            <Typography.LabelSm weight="semibold" color="text-secondary">
              {t("events.edit_modal.schedule_update_modal.original_series")}
            </Typography.LabelSm>
            <div className="flex items-center gap-2">
              <Icon
                type="16-calendar-recurring"
                size={16}
                className="text-default !w-4"
              />
              <Typography.BodySm color="text-secondary">
                {formattedOriginalSchedule}
              </Typography.BodySm>
            </div>
            {/* Arrow */}
            <div className="bg-primary absolute left-1/2 top-full flex -translate-x-1/2 -translate-y-1/2 justify-center rounded-full border p-1">
              <Icon type="16-arrow-down" size={16} className="!w-4" />
            </div>
          </div>

          {/* New Series */}
          <div className="border-primary relative flex flex-col gap-2 pb-2 pt-4">
            <Typography.LabelSm weight="semibold" color="text-secondary">
              {t("events.edit_modal.schedule_update_modal.new_series")}
            </Typography.LabelSm>
            <div className="flex items-center gap-2">
              <Icon
                type="16-calendar-recurring"
                size={16}
                className="text-default !w-4"
              />
              <Typography.BodySm color="text-secondary">
                {formattedNewSchedule}
              </Typography.BodySm>
            </div>
          </div>
        </div>

        {/* What attendees will experience Section */}
        <AttendeeExperienceSection />
      </div>
    </ConfirmationModal>
  );
};
